import React, { useState, useEffect, useCallback, useRef } from "react";
import { GiPathDistance } from "react-icons/gi";
import { IoTimerOutline } from "react-icons/io5";

import GeoImages from "../Constant/GeoImages";
import CustomSearch from "../Custom/CustomSearch";
import { IoBatteryHalfOutline } from "react-icons/io5";
import { CiLocationOn } from "react-icons/ci";
import { IoShareSocialSharp } from "react-icons/io5";
import liveTrackService from "../Services/liveTrackService";
import { googleMapsApiKey } from "../../config/clientsConfig";
import { showInfoToast, showSuccessToast } from "../../components/Constant/Notification";
import { IoBatteryDead } from "react-icons/io5";

const LiveStatusMember = ({onNavigationUserTracking}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextPageToken, setNextPageToken] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [locationNames, setLocationNames] = useState(new Map());
  const [geocodingLoading, setGeocodingLoading] = useState(new Set());
  const [error, setError] = useState(null);

  const scrollContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastFetchedQuery = useRef("");
  const isFetching = useRef(false);

  // Reverse geocoding function
  const getLocationName = async (lat, lng, pointId) => {
    if (geocodingLoading.has(pointId) || locationNames.has(pointId)) return;
    
    setGeocodingLoading(prev => new Set([...prev, pointId]));
    
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
      );
      const geocodeData = await response.json();

      if (geocodeData.status === "OK" && geocodeData.results.length > 0) {
        const result = geocodeData.results[0];
        const address = result.formatted_address;
        
        // Extract meaningful address components
        const addressComponents = result.address_components;
        let shortAddress = "";
        let area = "";
        let city = "";
        
        // Find area/locality and city
        for (const component of addressComponents) {
          if (component.types.includes('sublocality_level_1') || 
              component.types.includes('locality')) {
            if (!area) area = component.long_name;
          }
          if (component.types.includes('administrative_area_level_2') ||
              component.types.includes('administrative_area_level_1')) {
            if (!city) city = component.long_name;
          }
        }
        
        // Create a readable short address
        shortAddress = area && city ? `${area}, ${city}` : 
                      area ? area : 
                      city ? city : 
                      address.split(',').slice(0, 2).join(',').trim();
        
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: address,
          shortAddress: shortAddress,
          area: area,
          city: city
        })));
      } else {
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: "Unknown Location",
          shortAddress: "Unknown Location",
          area: "",
          city: ""
        })));
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationNames(prev => new Map(prev.set(pointId, {
        fullAddress: "Location not available",
        shortAddress: "Location not available",
        area: "",
        city: ""
      })));
    } finally {
      setGeocodingLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(pointId);
        return newSet;
      });
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback((query) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      if (query !== lastFetchedQuery.current) {
        lastFetchedQuery.current = query;
        fetchGeoLocations(query, null, true);
      }
    }, 600);
  }, []);

  
  const fetchGeoLocations = async (searchKey = "", nextId = null, isNewSearch = false) => {
    // Prevent multiple simultaneous calls
    if (isFetching.current) return;

    if (isNewSearch) {
      setLoading(true);
      setData([]);
      setNextPageToken(null);
      setHasMore(true);
      setError(null);
    } else {
      setIsLoadingMore(true);
    }

    isFetching.current = true;

    try {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];

      const requestBody = {
        date: formattedDate,
        searchKey: searchKey.trim(),
        limit: 10,
        next: nextId,
        direction: "next",
      };

      const response = await liveTrackService.getAllUsersGeoLocations(requestBody);
      
      if (response && response.data) {
        const { locations = [], next, previous } = response.data;
        
        // Transform API data to match component structure
        const transformedData = locations.map((location, index) => {
          const pointId = `user-${location.user?.value || index}-${location.geoPoint?.[0] || 0}-${location.geoPoint?.[1] || 0}`;
          return {
            id: location.user?.value || `user-${index}`,
            name: location.user?.label || "Unknown User",
            teamName: location.team?.label || "No Team",
            user : location.user || {},
            team : location.team || {},
            distance: location.travelDistance || "0KM",
            location: location.geoPoint && location.geoPoint.length === 2 
              ? `${location.geoPoint[0]}, ${location.geoPoint[1]}` 
              : "Location not available",
            isOnline: location.isLive || false,
            batteryPercentage: location.batteryPercentage || "0%",
            geoPoint: location.geoPoint || [],
            pointId: pointId,
            lastSeen: location.lastSeen || null,
            status: location.status || (location.isLive ? "Active" : "Inactive")
          };
        });

        // Start geocoding for locations with coordinates
        transformedData.forEach(item => {
          if (item.geoPoint.length === 2 && !locationNames.has(item.pointId)) {
            getLocationName(item.geoPoint[0], item.geoPoint[1], item.pointId);
          }
        });

        if (isNewSearch) {
          setData(transformedData);
        } else {
          setData(prevData => [...prevData, ...transformedData]);
        }

        setNextPageToken(next);
        setHasMore(!!next);

        // If no data in pagination, mark as no more
        if (!isNewSearch && locations.length === 0) {
          setHasMore(false);
        }
      } else {
        // Handle empty response
        if (isNewSearch) {
          setData([]);
          setError("No data received from server");
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching Type Latitude and longitudes:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch locations";
      setError(errorMessage);
      
      if (isNewSearch) {
        setData([]);
      }
      setHasMore(false);
    } finally {
      isFetching.current = false;
      if (isNewSearch) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching.current || !hasMore || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Add buffer to prevent multiple calls
    if (scrollTop + clientHeight >= scrollHeight - 200) { 
      fetchGeoLocations(searchQuery, nextPageToken, false);
    }
  }, [searchQuery, nextPageToken, hasMore, isLoadingMore]);

  // Initial load
  useEffect(() => {
    fetchGeoLocations("", null, true);
  }, []);

  // Attach scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleNavigateUserTracking = (team, user) => {
   onNavigationUserTracking(team, user);
  }

  const handleCopyMapLink = (geoPoint) => {
    const lat = geoPoint[0];
    const lng = geoPoint[1];

    if (!lat || !lng) {
      showInfoToast("Invalid coordinates for map link.");
      return;
    }
    const mapLink = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    
    navigator.clipboard.writeText(mapLink)
      .then(() => {
        showSuccessToast("Map link copied to clipboard!");
      })
      .catch(err => {
        console.error("Failed to copy map link:", err);
        
      });
  }

  return (
    <div className="bg-white rounded-3xl overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="bg-green-50 p-4 w-full flex-shrink-0">
        <div className="flex text-gray-700 items-center gap-2">
          <span className="text-lg">All Employees Live Status</span>
          {/* {data.length > 0 && (
            <span className="text-sm text-gray-500">({data.length} users)</span>
          )} */}
        </div>
        {/* <div className="flex text-gray-700 items-center gap-2 mt-1">
          <span className="text-sm">
            Live status updates every 5 minutes
          </span>
        </div> */}
      </div>

      <div className="w-full px-4 py-2 flex-shrink-0">
        <CustomSearch 
          placeholder="Search Employee Name" 
          searchTerm={searchQuery}
          handleSearchChange={(e) => handleSearchChange(e.target.value)}
          handleSearchSubmit={(e) => {
            e.preventDefault();
                   }}
        />
      </div>

      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 min-h-0"
        style={{ maxHeight: 'calc(100vh - 90px)' }}
      >
        {loading && data.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-customGreen mx-auto mb-4"></div>
              <p className="text-gray-500">Loading user locations...</p>
            </div>
          </div>
        ) : error && data.length === 0 ? (
          <div className="flex flex-col gap-3 items-center justify-center h-full min-h-[400px]">
            <img src={GeoImages.timeLineEmptyIcon} alt="Error" />
            <p className="text-red-500 text-sm font-medium">Error: {error}</p>
            <button 
              onClick={() => fetchGeoLocations("", null, true)}
              className="text-blue-500 text-sm hover:underline"
            >
              Try again
            </button>
          </div>
        ) : data.length > 0 ? (
          <>
            {data.map((member, index) => {
              const locationData = locationNames.get(member.pointId);
              const isLoadingLocation = geocodingLoading.has(member.pointId);
              
              return (
                <div
                  key={`${member.id}-${index}`}
                  className="bg-white border w-full rounded-lg p-4 mb-5 shadow-sm"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-[50px] h-[50px] rounded-full bg-customGreen flex items-center justify-center">
                        <span className="text-white text-sm">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[18px] text-gray-700 w-64 truncate text-wrap line-clamp-2 ">{member.name}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <div className={`w-fit border rounded-full px-2  ${
                            member.isOnline
                              ? "bg-green-100 border-green-500"
                              : "bg-red-100 border-red-500"
                          } flex items-center justify-center`}>
                            <span className={`text-[12px] ${
                              member.isOnline ? "text-green-500" : "text-red-500"
                            }`}>
                              {member.isOnline ? "Live" : "Inactive"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 text-[12px]">
                            {
                              member.batteryPercentage === "0%" ? (
                                <IoBatteryDead className="text-red-500 text-lg" />
                              ) : (
                                <IoBatteryHalfOutline className="text-orange-400 text-lg" />
                              )
                            }
                            {member.batteryPercentage} Battery
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-gray-700 text-base w-64 truncate text-wrap line-clamp-2">
                      {member.teamName}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex text-gray-700 items-center gap-2">
                        <GiPathDistance className="text-blue-500 text-lg" />
                        <span className="text-sm">Travelled Distance: {member.distance}</span>
                      </div>
                      <div className="flex text-gray-700 items-start gap-2">
                     
                        <div className="flex-1 flex text-gray-700 items- gap-2">
                        <CiLocationOn className="text-blue-500 text-lg" />
                          <span className="text-sm text-nowrap">Current Location: </span>
                          {isLoadingLocation ? (
                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-xs text-gray-500 italic">Loading address...</span>
                            </div>
                          ) : locationData && locationData.shortAddress !== "Unknown Location" ? (
                            <div title={locationData.fullAddress} className="cursor-pointer"> 
        
                              {locationData.fullAddress !== locationData.shortAddress && (
                                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                  {locationData.fullAddress.length > 35 
                                    ? `${locationData.fullAddress.substring(0, 50)}...` 
                                    : locationData.fullAddress}
                                </p>
                              )}
                            </div>
                          ) : member.geoPoint.length === 2 ? (
                            <div>
                              <p className="text-sm text-gray-600">Coordinates:</p>
                              <p className="text-xs text-gray-500 font-mono">{member.location}</p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Location not available</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div  className="flex items-center gap-3">
                      <button onClick={()=>{
                      handleNavigateUserTracking(member.team , member.user)
                    }} className="text-customGreen px-4 py-2 w-full text-sm border border-customGreen rounded-[10px] duration-200 hover:bg-customGreen hover:text-white">
                        Track Timeline
                      </button>
                      <button onClick={()=>handleCopyMapLink(member.geoPoint)} className="text-blue-700 bg-blue-100 px-4 py-2 text-sm border border-blue-500 rounded-[10px] duration-200 hover:bg-blue-200">
                        <IoShareSocialSharp className="text-xl" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen"></div>
                <span className="ml-2 text-gray-500">Loading more...</span>
              </div>
            )}
            
            {!hasMore && data.length > 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                No more users to load
              </div>
            )}

           
          </>
        ) : (
          <div className="flex flex-col gap-3 items-center justify-center h-full min-h-[400px]">
            <img src={GeoImages.timeLineEmptyIcon} alt="No data" />
            <p className="text-gray-500 text-sm">
              {searchQuery ? `No users found for "${searchQuery}"` : "No user location data available"}
            </p>
            {searchQuery && (
              <button 
                onClick={() => {
                  setSearchQuery("");
                  handleSearchChange("");
                }}
                className="text-blue-500 text-sm hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStatusMember;